import React from 'react';
import { format, isValid } from 'date-fns';
import { Bell, ExternalLink } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { displayPersonaScore, formatPersonaScore } from '@/lib/personaSignalScore';

const safeFormatDate = (dateValue) => {
  if (!dateValue) return 'Date unknown';
  try {
    const date = new Date(dateValue);
    if (!isValid(date)) return 'Date unknown';
    return format(date, 'dd MMMM yyyy');
  } catch (e) {
    return 'Date unknown';
  }
};

export default function ActivityTimeline({ signals }) {
  if (!signals || signals.length === 0) {
    return <p className="text-center text-sm text-[#5C6B7A]">No signals for this person.</p>;
  }

  const getImpactBadgeClass = (score) => {
    if (score > 0) return "bg-green-100 text-green-700";
    if (score === 0) return "bg-yellow-100 text-yellow-700";
    return "bg-amber-100 text-amber-800";
  };

  return (
    <div className="space-y-6">
      {signals.map((signal, index) => {
        const score = displayPersonaScore(signal)
          ?? (signal.impact_score != null && !Number.isNaN(Number(signal.impact_score))
            ? Number(signal.impact_score)
            : null);
        return (
        <div key={signal.id} className="flex gap-4">
          <div className="flex flex-col items-center">
            <div className="w-8 h-8 rounded-full bg-[#00C2C2]/20 flex items-center justify-center">
              <Bell className="w-4 h-4 text-[#00C2C2]" />
            </div>
            {index < signals.length - 1 && <div className="w-px h-full bg-gray-200 mt-2"></div>}
          </div>
          <div className="flex-1 pb-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  {signal.source_url ? (
                    <a 
                      href={signal.source_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="font-semibold text-[#00C2C2] hover:text-[#00A3A3] inline-flex items-center gap-1 hover:underline"
                    >
                      {signal.title}
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  ) : (
                    <p className="font-semibold text-[#0A1A2F]">{signal.title}</p>
                  )}
                </div>
                <p className="text-sm text-[#5C6B7A] mt-1">{signal.description}</p>
                <div className="flex items-center gap-2 mt-2">
                  {signal.signal_type && (
                    <Badge variant="outline" className="capitalize text-xs">
                      {signal.signal_type.replace('_', ' ')}
                    </Badge>
                  )}
                  {score != null && (
                    <Badge className={`${getImpactBadgeClass(score)} text-xs`}>
                      Impact: {formatPersonaScore(score)}
                    </Badge>
                  )}
                </div>
              </div>
              <p className="text-xs text-[#5C6B7A] ml-4 flex-shrink-0">
                {safeFormatDate(signal.signal_date)}
              </p>
            </div>
          </div>
        </div>
        );
      })}
    </div>
  );
}